import { Alert, AlertTitle, FormGroup, FormLabel, MenuItem, Select, TextField } from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { createUser, getUserById, PostUserRequest, updateUser, userTypes, userTypeLabels, UserTypes } from "src/apis/user";
import CompanySelect from "src/pages/components/CompanySelect";
import FormSaveAndResetButtons from "src/pages/components/FormSaveAndResetButtons";
import PageContainer from "src/pages/components/PageContainer";
import { isUserAdministrator } from "src/utils/auth";
import { Id } from "src/utils/types";

const initialValues: PostUserRequest = {
  email: '',
  id_empresa: 0,
  monthly_payment_value: 0,
  name: '',
  password: '',
  type: '',
}

const optionalKeysByUserType: {[key: string]: string[]} = {
  [userTypes.EMPRESA]: ["monthly_payment_value"],
  [userTypes.ADM_EMPRESA]: ["monthly_payment_value"],
  [userTypes.PSICOLOGO]: ["id_empresa", "profession"],
  [userTypes.ADMINISTRADOR]: ["id_empresa", "profession", "monthly_payment_value"],
  '': [],
}

const RegisterUser: FC = (): JSX.Element => {
  const [warning, setWarning] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<PostUserRequest>(initialValues);
  const { id } = useParams();

  const resetFormState = (): void => {
    setError('');
    setSuccess(false);
    setFormValues(initialValues);
  }

  const fetchUser = useCallback(async (userId: Id): Promise<void> => {
    if (!userId) return;

    try {
      const res = await getUserById(userId);

      setFormValues({
        email: res.email,
        id_empresa: res.company_id,
        monthly_payment_value: res.monthly_payment_value,
        name: res.name,
        password: '',
        type: res.type,
      })
      setError('');
    } catch(err: any) {
      setError(err?.message)
      setSuccess(false);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    try {
      if (Boolean(id)) {
        await updateUser(id, formValues);
      } else {
        await createUser(formValues);
      }

      setError('');
      setSuccess(true);
    } catch (err: any) {
      const emailAlreadyTaken = err?.response?.data?.error?.includes("already exists");
      if (emailAlreadyTaken) {
        setWarning(`O e-mail ${formValues?.email} já está sendo utilizado! Por favor tente novamente com outro e-mail`)
      } else {
        setError(err?.message);
      }

      setSuccess(false);
    } finally {
      setLoading(false);
    }
  }

  const isKeyOptionalForSelectedUserType = useCallback((key: string): boolean => {
    const optionalKeys = optionalKeysByUserType[formValues.type];

    return optionalKeys.includes(key);
  }, [formValues.type]);

  const isSubmitDisabled = useMemo(() => {
    if (success) {
      return true;
    }

    return !Object.entries(formValues).every(([key, value]) => {
      if (Boolean(id) && key === "password") return true;
      if (isKeyOptionalForSelectedUserType(key)) return true;

      return Boolean(value);
    })
  }, [id, success, formValues, isKeyOptionalForSelectedUserType]);

  useEffect(() => {
    fetchUser(id);
  }, [id, fetchUser])

  return (
    <PageContainer title={Boolean(id) ? "Editando usuário" : "Adicionar usuário"} error={error} guard={isUserAdministrator}>
      {Boolean(warning) && (
        <Alert severity="warning">
          <AlertTitle>
            {warning}
          </AlertTitle>
        </Alert>
      )}

      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        <FormGroup>
          <FormLabel>Nome Completo</FormLabel>
          <TextField
            name="name"
            type="text"
            value={formValues.name}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                name: e.target.value as string,
              }));
            }}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>Tipo de usuário</FormLabel>
          <Select
            labelId="type"
            name="type"
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                type: e.target.value as UserTypes,
              }));
              setSuccess(false);
            }}
            variant="outlined"
            value={formValues?.type ?? ''}
          >
            {Object.values(userTypes).map((value) => (
              <MenuItem key={value} value={String(value)}>
                {userTypeLabels[value] ?? value}
              </MenuItem>
            ))}
          </Select>
        </FormGroup>

        {(formValues.type === userTypes.EMPRESA ||
          formValues.type === userTypes.ADM_EMPRESA) && (
          <CompanySelect
            companyId={formValues.id_empresa}
              setCompanyId={(companyId: Id) => {
                setFormValues(previousValues => ({
                ...previousValues,
                id_empresa: companyId as number,
              }));
              setSuccess(false);
            }}
            setError={setError}
          />
        )}
        <FormGroup>
          <FormLabel>E-mail</FormLabel>
          <TextField
            name="email"
            type="text"
            value={formValues.email}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                email: e.target.value as string,
              }));
            }}
          />
        </FormGroup>
        <FormGroup>
          {Boolean(id) ? (
            <FormLabel>Senha (Deixar em branco para manter a senha atual)</FormLabel>
          ) : (
            <FormLabel>Senha</FormLabel>
          )}
          <TextField
            name="password"
            type="password"
            value={formValues.password}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                password: e.target.value as string,
              }));
            }}
          />
        </FormGroup>

        {formValues.type === userTypes.PSICOLOGO && (
          <FormGroup>
            <FormLabel>Valor por atendimento (R$)</FormLabel>
            <TextField
              name="monthly_payment_value"
              type="number"
              value={formValues.monthly_payment_value}
              onChange={(e) => {
                setFormValues(previousValues => ({
                  ...previousValues,
                  monthly_payment_value: parseFloat(e.target.value as string),
                }));
              }}
            />
          </FormGroup>
        )}

        <FormSaveAndResetButtons
          success={success}
          loading={loading}
          isSubmitDisabled={isSubmitDisabled}
          resetFormState={resetFormState}
          isResetFormStateDisabled={Boolean(id)}
        />
      </form>
    </PageContainer>
  )
}

export default RegisterUser;
